import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { getActiveWorldConfig } from '@gamecodex/world-config'
import { useLocale } from '@/i18n'

/** BCP-47 code → Open Graph `og:locale` (`en` → `en_US`). */
const OG_LOCALE: Record<string, string> = {
  en: 'en_US',
  de: 'de_DE',
  fr: 'fr_FR',
  es: 'es_ES',
  it: 'it_IT',
  pt: 'pt_PT',
  pl: 'pl_PL',
  ru: 'ru_RU',
  ja: 'ja_JP',
  ko: 'ko_KR',
  'zh-CN': 'zh_CN',
  'pt-BR': 'pt_BR',
}
const ogLocale = (code: string): string => OG_LOCALE[code] ?? `${code}_${code.toUpperCase()}`

export interface SeoProps {
  title?: string
  description?: string
  image?: string
  type?: 'website' | 'article'
  /** JSON-LD structured data (schema.org) — e.g. for entity pages. */
  jsonLd?: Record<string, unknown>
  /** Override the canonical path (defaults to the current location pathname). */
  canonicalPath?: string
  /** Set true on pages that should not be indexed (auth, dashboard, etc.). */
  noIndex?: boolean
}

export function Seo({
  title,
  description,
  image,
  type = 'website',
  jsonLd,
  canonicalPath,
  noIndex,
}: SeoProps) {
  const location = useLocation()
  const { locale, localeDef } = useLocale()
  // Per-world brand + canonical origin from the active world config. This is why
  // the shell is safe to share: zweihander.games emits its OWN canonical,
  // never grymwyrd.com (a cross-domain canonical would de-index the site).
  const { worldName: SITE, domain, seo, locales } = getActiveWorldConfig()
  const SITE_ORIGIN = `https://${domain}`
  const absolute = (pathOrUrl: string): string =>
    /^https?:\/\//.test(pathOrUrl) ? pathOrUrl : SITE_ORIGIN + (pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`)

  const desc = description ?? seo.defaultDescription
  const path = canonicalPath ?? location.pathname
  const canonical = absolute(path === '/' ? '/' : path.replace(/\/$/, ''))
  const fullTitle = title ? `${title} — ${SITE}` : `${SITE} — ${seo.tagline}`
  const imageUrl = absolute(image ?? seo.defaultOgImage)

  // hreflang: every route exists under a `/{locale}` prefix, so derive the
  // locale-agnostic base path (strip a leading supported-locale segment) and
  // re-prefix it per locale. `x-default` points at the world's default locale.
  // Skipped for noIndex pages — alternates on an unindexed page are noise.
  const supportedCodes = locales.supported.map((l) => l.code)
  const seg = path.split('/')[1] ?? ''
  const basePath = supportedCodes.includes(seg) ? path.slice(seg.length + 1) : path === '/' ? '' : path
  const localeHref = (code: string): string =>
    absolute(basePath === '' || basePath === '/' ? `/${code}` : `/${code}${basePath.replace(/\/$/, '')}`)
  const alternates = noIndex
    ? []
    : [...locales.supported.map((l) => ({ hreflang: l.code, href: localeHref(l.code) })), { hreflang: 'x-default', href: localeHref(locales.default) }]
  const ogAlternates = locales.supported.filter((l) => l.code !== locale).map((l) => ogLocale(l.code))

  return (
    <Helmet htmlAttributes={{ lang: locale, dir: localeDef.dir }}>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang alternates — one per supported locale + x-default */}
      {alternates.map((a) => (
        <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
      ))}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:locale" content={ogLocale(locale)} />
      {ogAlternates.map((l) => (
        <meta key={l} property="og:locale:alternate" content={l} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={imageUrl} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  )
}
